import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { KEYS } from '../../constants/keys.js';
import { isMantleComponentOfType, bindRefs } from '../../utils/react.js';
import { useTabContext, getPanelId, getTabId } from '../TabsContext/TabsContext.js';
import { TabsTab } from '../TabsTab/TabsTab.js';
import { StyledTabList } from './StyledTabList.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["children", "onChange"];
var TabsList = /*#__PURE__*/React.forwardRef(function TabsList2(props, ref) {
  var children = props.children,
      onChange = props.onChange,
      rest = _objectWithoutProperties(props, _excluded);

  var context = useTabContext();
  var tablistRef = React.useRef(null);
  var tabElements = React.useRef(void 0);
  var focusedTabIndex = React.useRef(-1);
  var contextValue = context.value;
  React.useEffect(function () {
    var _tablistRef$current;

    tabElements.current = (_tablistRef$current = tablistRef.current) === null || _tablistRef$current === void 0 ? void 0 : _tablistRef$current.querySelectorAll('[role="tab"]');
    var tabIndex = 0;
    var firstTabValue = void 0;
    var isContextValueFound = React.Children.toArray(children).some(function (child) {
      if (isMantleComponentOfType(child, TabsTab)) {
        var index = tabIndex++;
        firstTabValue = index === 0 ? child.props.value : firstTabValue;
        return contextValue === child.props.value;
      }

      return false;
    });

    if (!isContextValueFound) {
      onChange(firstTabValue);
    }
  }, [children, contextValue, onChange]);

  function setFocusIndex(index) {
    focusedTabIndex.current = index;
  }

  function focusTab(e, index) {
    e.preventDefault();
    setFocusIndex(index);
    var nextTab = tabElements.current ? tabElements.current[focusedTabIndex.current] : void 0;
    if (nextTab) nextTab.focus();
  }

  function handleKeydown(e) {
    e.stopPropagation();
    var tabsLength = tabElements.current.length;

    switch (e.key) {
      case KEYS.ARROW_LEFT:
        if (e.metaKey) {
          focusTab(e, 0);
        } else {
          focusTab(e, (focusedTabIndex.current - 1 + tabsLength) % tabsLength);
        }

        break;

      case KEYS.ARROW_RIGHT:
        if (e.metaKey) {
          focusTab(e, tabsLength - 1);
        } else {
          focusTab(e, (focusedTabIndex.current + 1) % tabsLength);
        }

        break;

      case KEYS.HOME:
        focusTab(e, 0);
        break;

      case KEYS.END:
        focusTab(e, tabsLength - 1);
        break;
    }
  }

  function handleOnChange(value) {
    if (context && value !== context.value) {
      onChange(value);
    }
  }

  var childTabIndex = 0;
  return /*#__PURE__*/jsx(StyledTabList, _objectSpread(_objectSpread({}, rest), {}, {
    onKeyDown: handleKeydown,
    ref: bindRefs(tablistRef, ref),
    role: "tablist",
    selectedTabSelector: "[aria-selected=\"true\"]",
    children: React.Children.map(children, function (child) {
      if (isMantleComponentOfType(child, TabsTab)) {
        var index = childTabIndex++;
        var selected = context.value === child.props.value;
        return /*#__PURE__*/React.cloneElement(child, {
          "aria-controls": getPanelId(context, child.props.value),
          "aria-selected": selected ? true : void 0,
          id: getTabId(context, child.props.value),
          tabIndex: selected ? 0 : -1,
          onClick: function onClick(e) {
            setFocusIndex(index);
            handleOnChange(child.props.value);
            if (typeof child.props.onClick === "function") child.props.onClick(e);
          },
          onFocus: function onFocus(e) {
            if (selected) setFocusIndex(index);
            if (typeof child.props.onFocus === "function") child.props.onFocus(e);
          },
          onKeyDown: function onKeyDown(e) {
            if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
              e.preventDefault();
              handleOnChange(child.props.value);
            }

            if (typeof child.props.onKeyDown === "function") child.props.onKeyDown(e);
          }
        });
      }

      return child;
    })
  }));
});
TabsList.propTypes = {
  "children": _PropTypes.node,
  "onChange": _PropTypes.func.isRequired
};
TabsList.displayName = "TabsList";

export { TabsList };
